import { useCallback } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { sanitizeMetadata, validateEventName } from '../sanitizer';

import { useFeatureGate } from './useFeatureGate';

/**
 * Core hook for tracking events to Statsig with HIPAA-compliant sanitization
 */
export function useTrackEvent() {
  const { client } = useStatsigClient();
  const showAnalyticsLogs = useFeatureGate('feature_show_analytics_logs');

  const trackEvent = useCallback(
    (eventName: string, options?: { value?: number; metadata?: Record<string, unknown> }) => {
      try {
        // Validate event name doesn't contain PHI
        if (!validateEventName(eventName)) {
          console.error(`[Analytics] Invalid event name: ${eventName}`);
          return;
        }

        // Sanitize metadata to ensure HIPAA compliance
        const sanitizedMetadata = sanitizeMetadata(options?.metadata);

        // Log the event to Statsig
        client.logEvent(eventName, options?.value, sanitizedMetadata);

        // Log in development for debugging
        if (import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev' || showAnalyticsLogs) {
          console.debug('[Analytics] Event tracked:', {
            eventName,
            value: options?.value,
            metadata: sanitizedMetadata,
          });
        }
      } catch (error) {
        console.error('[Analytics] Failed to track event:', error);
      }
    },
    [client, showAnalyticsLogs],
  );

  const trackError = useCallback(
    (error: unknown, context?: string) => {
      trackEvent('error_occurred', {
        metadata: {
          error_message: error instanceof Error ? error.message : String(error),
          error_type: context || 'unknown',
          timestamp: new Date().toISOString(),
        },
      });
    },
    [trackEvent],
  );

  const trackTiming = useCallback(
    (label: string, duration: number, metadata?: Record<string, unknown>) => {
      trackEvent('performance_timing', {
        value: Math.round(duration),
        metadata: {
          action_type: label,
          duration_ms: String(Math.round(duration)),
          ...metadata,
        },
      });
    },
    [trackEvent],
  );

  return {
    trackEvent,
    trackError,
    trackTiming,
  };
}
